import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { CompositeNavigationProp } from '@react-navigation/native';
import { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import AppShell from '../components/layout/AppShell';
import SegmentedControl from '../components/common/SegmentedControl';
import ChatCard from '../components/chats/ChatCard';
import GroupChatCard from '../components/chats/GroupChatCard';
import EmptyState from '../components/work/EmptyState';
import { RootStackParamList, BottomTabParamList, ClubsStackParamList } from '../types/navigation';
import { ChatView } from '../types/chats';
import { useChat } from '../context/ChatContext';
import { useTheme } from '../theme/ThemeContext';

type ChatsNavigationProp = CompositeNavigationProp<
  StackNavigationProp<RootStackParamList, 'Chats'>,
  CompositeNavigationProp<
    BottomTabNavigationProp<BottomTabParamList>,
    StackNavigationProp<ClubsStackParamList>
  >
>;

const ChatsScreen: React.FC = () => {
  const navigation = useNavigation<ChatsNavigationProp>();
  const { theme } = useTheme();
  const { conversations, loading, fetchConversations, refreshConversations } = useChat();
  const [selectedView, setSelectedView] = useState<ChatView>('chats');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await refreshConversations();
    setRefreshing(false);
  };

  const handleChatPress = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation && conversation.type === 'personal' && conversation.participants.length > 0) {
      const otherUser = conversation.participants[0];
      navigation.navigate('PersonalChat', { 
        userId: otherUser.id, 
        userName: otherUser.name,
        conversationId,
      });
    }
  };

  const handleGroupChatPress = (clubId: string, conversationId?: string) => {
    if (!clubId) {
      console.warn('Cannot open club chat without clubId');
      return;
    }

    navigation.navigate('MainTabs', {
      screen: 'Clubs',
      params: {
        screen: 'ClubChat',
        params: { clubId, conversationId },
      },
    });
  };

  const renderChats = () => {
    const personalChats = conversations.filter(c => c.type === 'personal');

    if (personalChats.length === 0) {
      return <EmptyState icon="chatbubble-outline" message="No conversations yet" />;
    }

    return (
      <FlatList
        data={personalChats}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          const otherUser = item.participants[0];
          const timestampString = item.lastMessage?.createdAt || item.updatedAt;
          const timestamp = timestampString ? new Date(timestampString) : new Date();
          
          return (
            <ChatCard 
              chat={{
                id: item.id,
                userId: otherUser?.id || '',
                userName: otherUser?.name || 'Unknown',
                userAvatar: otherUser?.photo,
                lastMessage: item.lastMessage?.content || '',
                lastMessageTime: timestamp,
                unreadCount: item.unreadCount,
                isOnline: false,
              }} 
              onPress={() => handleChatPress(item.id)} 
            />
          );
        }}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      />
    );
  };

  const renderGroups = () => {
    // Filter to only show group chats where:
    // 1. Club still exists (club is not null)
    // 2. User is still a member (chatEnabled check ensures club is active)
    const groupChats = conversations.filter(c => 
      c.type === 'group' && 
      c.club !== null && 
      c.club !== undefined
    );

    if (groupChats.length === 0) {
      return <EmptyState icon="people-outline" message="No group chats yet" />;
    }

    const groupCards = groupChats.map((item) => {
      const timestampString = item.lastMessage?.createdAt || item.updatedAt;
      const timestamp = timestampString ? new Date(timestampString) : new Date();

      return {
        id: item.id,
        clubId: item.clubId || '',
        clubName: item.club?.name || item.name || 'Group Chat',
        lastMessage: item.lastMessage?.content || 'Start the conversation',
        lastMessageTime: timestamp,
        unreadCount: item.unreadCount,
        memberCount: item.memberCount || item.participants.length + 1,
        chatEnabled: item.club?.chatEnabled,
      };
    });

    return (
      <FlatList
        data={groupCards}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <GroupChatCard 
            groupChat={item} 
            onPress={() => handleGroupChatPress(item.clubId, item.id)} 
          />
        )}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      />
    );
  };

  const renderContent = () => {
    switch (selectedView) {
      case 'chats':
        return renderChats();
      case 'groups':
        return renderGroups();
      default:
        return renderChats();
    }
  };

  return (
    <AppShell title="Chats" scrollable={false} showBackButton={true}>
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <SegmentedControl
          segments={[
            { value: 'chats' as const, label: 'Mails' },
            { value: 'groups' as const, label: 'Circles' },
          ]}
          selectedValue={selectedView}
          onValueChange={setSelectedView}
        />
        <View style={styles.content}>
          {renderContent()}
        </View>
      </View>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  listContent: {
    paddingTop: 8,
    paddingBottom: 20,
  },
});

export default ChatsScreen;
